import { useCallback, useMemo, type ReactNode } from 'react';
import type {
  ToolApprovalCounts,
  ToolApprovalFilter,
  ToolApprovalScope,
  ToolApprovalTab,
} from '@kirin/api-client/contracts/shadow-ai-tool-approvals';
import type {
  BulkToolbarContext,
  DataTableProps,
  RowSelectionOptions,
} from '@/components/DataTable';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';
import { useOrgWriteGate, type OrgWriteGate } from '@/hooks/useRoleCheck';
import { useToolApprovalCounts } from '@/hooks/api/useShadowAiToolApprovals';
import { ToolApprovalBulkBar } from './ToolApprovalBulkBar';
import type { ToolApprovalBulkRow } from './toolApprovalBulkIds';
import { useToolApprovalShowFilter } from './useToolApprovalShowFilter';

export interface UseToolApprovalTableOptions<TRow extends ToolApprovalBulkRow> {
  organizationId: string;
  /** DataTable id — keys the Show filter's URL param. */
  tableId: string;
  tab: ToolApprovalTab;
  /** Approval scope the bulk actions write to. */
  scope: ToolApprovalScope;
  /**
   * Row identity for bulk selection — must match the table's row-expansion
   * `getRowId`. Pass a stable (module-level) function.
   */
  getRowId: (row: TRow) => string;
  /** Tool id the approve/unapprove endpoints key on. Stable, as `getRowId`. */
  getToolId: (row: TRow) => string;
  /**
   * Human name of a row for its checkbox's accessible name ("Select <name>").
   * Stable, as `getRowId`.
   */
  getRowLabel: (row: TRow) => string;
  /**
   * Tab-owned filters outside the DataTable (e.g. Browser evidence filters)
   * that swap the result set — folded into the selection reset alongside the
   * Show filter.
   */
  selectionResetKey?: string;
  /** KIR-3495 user-scope drill-down (URL state off; counts scoped). */
  scopeUserId?: string;
  /** Poll counts with the table (Running Now polls faster). */
  countsRefetchInterval?: number;
}

/** Props spread onto the tab's `<DataTable>`; all undefined when flag is off. */
export interface ToolApprovalDataTableProps<TRow extends ToolApprovalBulkRow> {
  toolbarEndSlot?: ReactNode;
  onClearFilters?: () => void;
  /**
   * KIR-5088: the Show filter's URL param, so "Clear filters" drops it in the
   * table's own single search-params write instead of racing `onClearFilters`.
   *
   * Declared here rather than left to the spread onto `<DataTable>`: TypeScript
   * skips excess-property checks on spread members, so an undeclared prop
   * type-checks whether or not `DataTableProps` still accepts it. That would
   * make this fix invisible to the compiler — rename or typo the prop and
   * `tsc` stays green while the Show filter silently survives "Clear filters"
   * again, which is the exact bug it exists to prevent.
   */
  clearFiltersUrlKeys?: DataTableProps<TRow>['clearFiltersUrlKeys'];
  pageResetKey?: string;
  /** KIR-4855 bulk selection — Owner / Manager / Support only. */
  rowSelection?: RowSelectionOptions<TRow>;
}

export interface ToolApprovalTableState<TRow extends ToolApprovalBulkRow> {
  /** `shadow_ai_tool_approval` flag (false while flags load). */
  enabled: boolean;
  /**
   * True until the flag resolves. Tabs hold the table back meanwhile so the
   * first request already carries the Show filter (no fetch-then-refetch, and
   * no spurious page reset of a deep-linked page).
   */
  isFlagLoading: boolean;
  /**
   * Show filter to send as the list `approval` param — undefined when the flag
   * is off, so the request (and query key) stays exactly as before.
   */
  approvalFilter: ToolApprovalFilter | undefined;
  writeGate: OrgWriteGate;
  counts: ToolApprovalCounts | undefined;
  dataTableProps: ToolApprovalDataTableProps<TRow>;
}

/**
 * KIR-4854: everything an org Shadow AI Tools tab needs for tool approval —
 * flag gate, the Show filter (state, counts, toolbar control, Clear-filters
 * reset) and the org write gate for the Approval column.
 * KIR-4855: plus row selection with the bulk Approve / Unapprove bar.
 */
export function useToolApprovalTable<TRow extends ToolApprovalBulkRow>({
  organizationId,
  tableId,
  tab,
  scope,
  getRowId,
  getToolId,
  getRowLabel,
  selectionResetKey,
  scopeUserId,
  countsRefetchInterval,
}: UseToolApprovalTableOptions<TRow>): ToolApprovalTableState<TRow> {
  const { flags, isLoading: isFlagLoading } = useFeatureFlags([
    flagsmithFeatureFlags.SHADOW_AI_TOOL_APPROVAL,
  ]);
  const enabled = flags[flagsmithFeatureFlags.SHADOW_AI_TOOL_APPROVAL] ?? false;

  // useOrgWriteGate returns a fresh object each render; the tabs memoize their
  // columns on this, so keep it referentially stable (column churn resets the
  // dynamic filter dropdowns).
  const gate = useOrgWriteGate(organizationId);
  const writeGate = useMemo<OrgWriteGate>(
    () => ({
      hidden: gate.hidden,
      disabled: gate.disabled,
      isLoading: gate.isLoading,
    }),
    [gate.hidden, gate.disabled, gate.isLoading]
  );
  // The org-wide tables keep the Show choice in the URL; a member drill-down
  // keeps it in component state (there is no shareable URL for it).
  const persistInUrl = !scopeUserId;
  const { data: counts } = useToolApprovalCounts(organizationId, tab, {
    enabled,
    scopeUserId,
    refetchInterval: countsRefetchInterval,
  });
  const showFilter = useToolApprovalShowFilter({
    enabled,
    tableId,
    persistInUrl,
    counts,
  });
  const { filter } = showFilter;

  // Members get no selection at all; Support selects but the bar's actions are
  // disabled ("disable, don't hide"). Held back while the role resolves so the
  // checkbox column doesn't flash in for a member.
  const selectionEnabled = enabled && !writeGate.hidden && !writeGate.isLoading;
  const getRowAriaLabel = useCallback(
    (row: TRow): string => `Select ${getRowLabel(row)}`,
    [getRowLabel]
  );
  const rowSelection = useMemo<RowSelectionOptions<TRow> | undefined>(() => {
    if (!selectionEnabled) return undefined;
    return {
      getRowId,
      getRowAriaLabel,
      // Anything that swaps the result set without touching the DataTable's
      // own filters or search clears the selection: the Show filter, tab-owned
      // filters, and the org / drill-down member — the table is not remounted
      // on those, and row ids (domain, toolId) repeat across them, so a stale
      // selection would otherwise approve in the wrong org.
      resetToken: JSON.stringify([
        organizationId,
        scopeUserId ?? null,
        filter,
        selectionResetKey ?? null,
      ]),
      renderBulkToolbar: (ctx: BulkToolbarContext<TRow>) => (
        <ToolApprovalBulkBar<TRow>
          ctx={ctx}
          organizationId={organizationId}
          scope={scope}
          getToolId={getToolId}
          disabled={writeGate.disabled}
        />
      ),
    };
  }, [
    selectionEnabled,
    getRowId,
    getRowAriaLabel,
    filter,
    selectionResetKey,
    scopeUserId,
    organizationId,
    scope,
    getToolId,
    writeGate.disabled,
  ]);

  const dataTableProps = useMemo<ToolApprovalDataTableProps<TRow>>(() => {
    if (!enabled) return {};
    return { ...showFilter.dataTableProps, rowSelection };
  }, [enabled, showFilter.dataTableProps, rowSelection]);

  return {
    enabled,
    isFlagLoading,
    approvalFilter: enabled ? filter : undefined,
    writeGate,
    counts,
    dataTableProps,
  };
}
